import React from 'react';
import { CompanyListItem, ICompany } from 'types/company';
import { TinyCommunity } from 'types/company/community';
import { UserFeedData } from '../useUserFeedSWR';
import ShowRoomUpdate from 'pages/Rooms/Updates/Show/Update';
import CommunityUpdate from 'pages/Community/Overview/CommunityUpdates/Update';
import BoardroomUpdate from 'pages/Boardroom/Updates/Update';
import { communityUrls, companyUrls } from 'urls';
import Update from 'pages/Company/Overview/sections/updates/Show/Update';
import { asBoardRoomAuthor, asCompanyAuthor, asRoomAuthor } from 'domain/shared/author';
import { getPostedBy } from 'pages/Community/Overview/CommunityUpdates/UpdateList';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { bluePlanetTheme } from 'ui/theme';
import { halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import StarIcon from 'ui/elements/icons/StarIcon';
import { fontSizeTiny } from 'ui/theme/themeBluePlanet';

const SuggestionReasonContainer = styled.div`
  background-color: ${bluePlanetTheme.bluePlanetPalette.grey.light};
  margin-top: ${halfSpacing};
  display: flex;
  align-items: center;
  gap: ${quarterSpacing};
  ${bluePlanetTheme.breakpoints.down('sm')} {
    margin-left: ${halfSpacing};
  }
`;

const SuggestionText = styled.div`
  font-size: ${fontSizeTiny};
  color: ${bluePlanetTheme.bluePlanetPalette.grey.main};
  display: flex;
  align-items: center;
  gap: ${quarterSpacing};
`;

const StyledStarIcon = styled(StarIcon)`
  color: ${bluePlanetTheme.bluePlanetPalette.yellow.main};
  font-size: ${fontSizeTiny};
`;

const highlightFlash = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const UpdateWrapper = styled.div<{ isNewlyPosted?: boolean }>`
  ${props =>
    props.isNewlyPosted &&
    css`
      animation: ${highlightFlash} 0.3s ease-out;
    `}
`;

function formatSuggestionReason(suggestionReason: string): string | null {
  try {
    const parsed = JSON.parse(suggestionReason);

    if (parsed.type === 'industry-match' && parsed.industry) {
      return `Because you're interested in ${parsed.industry}`;
    }

    return 'Because it matches your interests';
  } catch {
    // If JSON parsing fails, return null to indicate no suggestion should be shown
    return null;
  }
}

const ShowUpdate = React.forwardRef<
  HTMLDivElement,
  {
    update: UserFeedData;
    communities: TinyCommunity[];
    companies: CompanyListItem[];
    companyProfiles: ICompany[];
    isNewlyPosted?: boolean;
  }
>(({ communities, companies, update, companyProfiles, isNewlyPosted }, ref) => {
  if (update.type === 'room') {
    const room = update.roomUpdate.room;
    const company = companies.find(c => c.id === room.companyId);
    const viewRoomHref = company && room && companyUrls.rooms.view(company.slug, room.id);
    const viewCompanyHref = company && companyUrls.overview(company.slug, 'profile');
    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <ShowRoomUpdate
          key={`update-${update.roomUpdate.update.id}`}
          update={update.roomUpdate}
          creator={update.roomUpdate.update.createdBy}
          onChange={() => {}}
          canEditPost={false}
          author={asRoomAuthor(update.roomUpdate.update.createdBy, company, viewCompanyHref, room?.name, viewRoomHref)}
        />
      </UpdateWrapper>
    );
  } else if (update.type === 'boardroom') {
    const maybeCompany = companies.find(c => c.id === update.boardroomUpdate.company.id);
    const maybeCommunity = communities.find(c => c.id === update.boardroomUpdate.company.id);
    const companyHref = maybeCommunity
      ? communityUrls.overview(maybeCommunity.slug)
      : maybeCompany
        ? companyUrls.overview(maybeCompany.slug, 'updates')
        : undefined;
    const roomHref = maybeCommunity
      ? communityUrls.overview(maybeCommunity.slug)
      : maybeCompany
        ? companyUrls.boardroom.overview(maybeCompany.slug)
        : undefined;

    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <BoardroomUpdate
          key={`update-${update.boardroomUpdate.update.id}`}
          boardroomUpdate={update.boardroomUpdate}
          creator={update.boardroomUpdate.update.createdBy}
          companyId={update.boardroomUpdate.company.id}
          canEditPost={false}
          onChange={() => {}}
          author={asBoardRoomAuthor(
            update.boardroomUpdate.update.createdBy,
            maybeCompany || maybeCommunity,
            companyHref,
            roomHref,
          )}
        />
      </UpdateWrapper>
    );
  } else if (update.type === 'community') {
    const community = communities.find(c => c.id === update.communityUpdate.communityId);
    if (!community) return null;

    const postedBy = getPostedBy(update.communityUpdate, companyId =>
      communityUrls.companies.viewFromOverview(community.slug, companyId, 'community-feed'),
    );

    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <CommunityUpdate
          key={`update-${update.communityUpdate.update.id}`}
          update={update.communityUpdate}
          creator={update.communityUpdate.update.createdBy}
          canEditPost={false}
          onEdit={() => {}}
          community={community}
          onArchive={() => {}}
          postedBy={postedBy}
          shownIn="dashboard"
        />
      </UpdateWrapper>
    );
  } else if (update.type === 'company') {
    const company =
      companyProfiles.find(c => c.id === update.companyUpdate.companyId) ||
      companies.find(c => c.id === update.companyUpdate.companyId);

    const suggestionReasonText = update.companyUpdate.suggestionReason
      ? formatSuggestionReason(update.companyUpdate.suggestionReason)
      : null;

    return company ? (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        {suggestionReasonText && (
          <SuggestionReasonContainer>
            <SuggestionText>
              <StyledStarIcon />
              Suggested: {suggestionReasonText}
            </SuggestionText>
          </SuggestionReasonContainer>
        )}
        <Update
          key={`update-${update.companyUpdate.update.id}`}
          update={update.companyUpdate}
          creator={update.companyUpdate.update.createdBy}
          canEditPost={false}
          company={company}
          author={asCompanyAuthor(
            update.companyUpdate.update.createdBy,
            company,
            company ? companyUrls.overview(company.slug, 'updates') : undefined,
          )}
          onArchive={() => {}}
          onEdit={() => {}}
          isAtProfile={false}
        />
      </UpdateWrapper>
    ) : null;
  }
  return null;
});

ShowUpdate.displayName = 'ShowUpdate';

export default ShowUpdate;
